import { renderToString } from "react-dom/server";
import { cloneElement, ReactElement } from "react";

/**
 * Creates a Data URI for a custom SVG map pin containing a Lucide icon.
 * This ensures the map renders crisp, scalable, and premium markers instead of standard emojis.
 * 
 * @param color The background color of the map pin.
 * @param iconElement The Lucide React component instance (e.g., <Coffee />)
 * @returns A data:image/svg+xml URI string suitable for google.maps.Marker icon.url
 */
export function createPoiMarkerUrl(color: string, iconElement: ReactElement): string {
  // Render the Lucide icon to an SVG string, forcing color to white and a specific size
  const iconSvgString = renderToString(
    cloneElement(iconElement, {
      color: "#FFFFFF",
      size: 16,
      strokeWidth: 2.5,
    })
  );

  // Outer Map Pin SVG (a nice teardrop shape)
  // ViewBox 0 0 32 40. Teardrop path:
  // Starts at bottom center (16, 40), curves up to a circle of radius 16 at (16, 16)
  const pinSvg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="46" viewBox="0 0 32 42">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000000" flood-opacity="0.3" />
        </filter>
      </defs>
      <!-- Pin background -->
      <path 
        d="M16 0C7.163 0 0 7.163 0 16c0 11.5 16 26 16 26s16-14.5 16-26C32 7.163 24.837 0 16 0z" 
        fill="${color}" 
        filter="url(#shadow)"
      />
      <!-- Inner white circle for contrast (optional, but looks premium) -->
      <!-- <circle cx="16" cy="16" r="11" fill="rgba(255,255,255,0.15)" /> -->
      
      <!-- Render the injected Lucide SVG centered -->
      <g transform="translate(8, 8)">
        ${iconSvgString}
      </g>
    </svg>
  `;

  // Return base64 encoded data URI to avoid URL encoding issues with certain characters
  return `data:image/svg+xml;base64,${btoa(pinSvg)}`;
}
